import React from 'react';
import { Linkedin, Twitter, Instagram, Youtube } from 'lucide-react';
import { fileUrl } from '@/lib/api';
import { useMagnetic } from '@/lib/motion';
import GradientMesh from '@/components/GradientMesh';
import PresentingSponsorBadge from '@/components/PresentingSponsorBadge';

const DEFAULT_REGISTER_URL = 'https://circle.swoogo.com/SEI_ConIII';
const DEFAULT_LEGAL = [
  { label: 'Privacy', href: '/privacy' },
  { label: 'Terms', href: '/terms' },
  { label: 'FAQ', href: '/#faq' },
  { label: 'Contact', href: '/contact' },
];

export default function Footer({ footer = {}, presentingSponsor, motion = {} }) {
  const h1 = footer.headline_line1 || 'THE FUTURE OF SPORT, MEDIA, AND INNOVATION';
  const h2 = footer.headline_line2 || 'HAPPENS HERE.';
  const h1Orange = !!footer.headline_line1_orange;
  const h2Orange = footer.headline_line2_orange !== false;
  const bg = fileUrl(footer.image_url) || 'https://images.unsplash.com/photo-1581351721010-8cf859cb14a4?w=1600&q=80&auto=format&fit=crop';
  const socials = footer.socials || { linkedin: '#', twitter: '#', instagram: '#', youtube: '#' };
  const meshEnabled = motion?.wow?.gradient_mesh_footer !== false;
  const magneticEnabled = motion?.wow?.magnetic_cursor !== false;
  const registerBtnRef = useMagnetic(magneticEnabled);

  const registerLabel = footer.register_label || 'Register Now';
  const registerUrl = footer.register_url || DEFAULT_REGISTER_URL;
  const eventLine = footer.meta_event_line || 'July 7–9, 2026  •  Bellagio Hotel & Casino  •  Las Vegas, NV';
  const copyright = footer.meta_copyright || '© 2026 SEICon. All rights reserved.';
  const logoMain = footer.meta_logo_main || 'SEI / CON III';
  const tagline = footer.meta_tagline || 'Sports • Entertainment • Innovation';
  // Admin-stored footer links may still carry the old '/faq' route, which
  // does not exist as a page — the FAQ is a homepage section. Rewrite it
  // to '/#faq' at render time (same precedent as the nav's HOTEL rewrite)
  // so the fix holds regardless of what is saved in the database.
  const legalRaw = Array.isArray(footer.legal_links) && footer.legal_links.length > 0 ? footer.legal_links : DEFAULT_LEGAL;
  const legal = legalRaw.map((l) => (l && l.href === '/faq' ? { ...l, href: '/#faq' } : l));

  return (
    <footer>
      <section
        className="relative bg-card-dark overflow-hidden"
        data-testid="footer-cta"
      >
        {meshEnabled && <GradientMesh enabled={meshEnabled} />}
        <div className="container-x text-center py-14 md:py-20 relative z-10">
          {presentingSponsor && presentingSponsor.enabled && presentingSponsor.show_in_footer !== false && (
            <div className="mb-8 md:mb-10 flex justify-center">
              <PresentingSponsorBadge
                sponsor={presentingSponsor}
                eyebrow={presentingSponsor.hero_eyebrow_label}
                theme="dark"
                size="md"
                testid="footer-presenting-sponsor"
                align="center"
              />
            </div>
          )}
          <h2 className="font-display font-bold uppercase text-white leading-[1.05] text-[32px] md:text-[44px] lg:text-[56px] tracking-tightish">
            <span className={h1Orange ? 'text-brand-orange' : 'text-white'}>{h1}</span>
            <br />
            <span className={h2Orange ? 'text-brand-orange' : 'text-white'}>{h2}</span>
          </h2>
          <div className="mt-10">
            <span ref={registerBtnRef} className="inline-block">
              <a href={registerUrl} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="footer-register">{registerLabel}</a>
            </span>
          </div>
        </div>
      </section>

      <div className="bg-card-dark text-white">
        <div className="container-x py-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img
                src="/logo-white.png"
                alt={`SEI CON — ${tagline}`}
                className="h-8 w-auto"
                decoding="async"
                data-testid="footer-logo-main"
              />
            </div>

            <div className="font-body font-medium text-[11px] uppercase text-white/70 tracking-wider2 text-center md:text-left" data-testid="footer-event-line">
              {eventLine}
            </div>

            <div className="flex items-center gap-4 text-white/70">
              <a href={socials.linkedin || '#'} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="hover:text-white transition"><Linkedin size={18} /></a>
              <a href={socials.twitter || '#'} target="_blank" rel="noopener noreferrer" aria-label="X" className="hover:text-white transition"><Twitter size={18} /></a>
              <a href={socials.instagram || '#'} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="hover:text-white transition"><Instagram size={18} /></a>
              <a href={socials.youtube || '#'} target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="hover:text-white transition"><Youtube size={18} /></a>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3 font-body text-[11px] text-white/60">
            <div data-testid="footer-copyright">{copyright}</div>
            <div className="flex items-center gap-3 uppercase tracking-wider2" data-testid="footer-legal">
              {legal.map((l, i) => (
                <React.Fragment key={`${l.label}-${i}`}>
                  <a href={l.href || '#'} className="hover:text-white">{l.label}</a>
                  {i < legal.length - 1 && <span>•</span>}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
